import React, { useEffect, useMemo, useState } from "react";
import { awinApi } from "../services/externalPlatformApi";

const EMPTY_LINK = {
  destination: "Ibiza",
  category: "hotel",
  partner_name: "lastminute.com",
  display_title: "",
  display_subtitle: "",
  awin_url: "",
  commission_pct: 5,
  affiliate_share: 2,
  hlt_share: 3,
  active: true,
  sort_order: 0,
};

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value || 0));

export default function AdminAwinLinks() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(EMPTY_LINK);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sampleValue, setSampleValue] = useState(500);

  const calculator = useMemo(() => {
    const sale = Number(sampleValue || 0);
    const commission = sale * (Number(form.commission_pct || 0) / 100);
    const affiliate = sale * (Number(form.affiliate_share || 0) / 100);
    return {
      commission,
      affiliate,
      hlt: commission - affiliate,
    };
  }, [form.affiliate_share, form.commission_pct, sampleValue]);

  useEffect(() => {
    loadLinks();
  }, []);

  async function loadLinks() {
    setLoading(true);
    setError("");
    try {
      const payload = await awinApi.listLinks({ includeInactive: true, limit: 500 });
      setLinks(Array.isArray(payload?.links) ? payload.links : []);
    } catch {
      setError("Could not load Awin links. The backend endpoint may not be live yet.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(name, value) {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "commission_pct" || name === "affiliate_share") {
        next.hlt_share = Number(next.commission_pct || 0) - Number(next.affiliate_share || 0);
      }
      return next;
    });
  }

  function editLink(link) {
    setEditingId(link.id);
    setForm({
      destination: link.destination || "Ibiza",
      category: link.category || "hotel",
      partner_name: link.partner_name || "lastminute.com",
      display_title: link.display_title || "",
      display_subtitle: link.display_subtitle || "",
      awin_url: link.awin_url || "",
      commission_pct: link.commission_pct ?? 5,
      affiliate_share: link.affiliate_share ?? 2,
      hlt_share: link.hlt_share ?? 3,
      active: link.active !== false,
      sort_order: link.sort_order || 0,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_LINK);
  }

  async function saveLink(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      commission_pct: Number(form.commission_pct || 0),
      affiliate_share: Number(form.affiliate_share || 0),
      hlt_share: Number(form.hlt_share || 0),
      sort_order: Number(form.sort_order || 0),
    };

    try {
      if (editingId) {
        await awinApi.updateLink(editingId, payload);
      } else {
        await awinApi.createLink(payload);
      }
      resetForm();
      await loadLinks();
    } catch {
      setError("Could not save the Awin link. Check required fields and backend availability.");
    } finally {
      setSaving(false);
    }
  }

  async function removeLink(id) {
    if (!window.confirm("Remove this affiliate link from the public site?")) return;
    try {
      await awinApi.removeLink(id);
      await loadLinks();
    } catch {
      setError("Could not remove the link.");
    }
  }

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Awin Links</h1>
            <p className="mt-1 text-slate-300">
              Manage Last Minute, Skiddle and travel affiliate links per destination.
            </p>
          </div>
          <span className="badge bg-brand-600/20 text-slate-100">
            {links.length} link{links.length === 1 ? "" : "s"}
          </span>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      <section className="grid gap-6 lg:grid-cols-[380px,1fr]">
        <form className="card space-y-4" onSubmit={saveLink}>
          <div>
            <h2 className="text-xl font-semibold">
              {editingId ? "Edit affiliate link" : "Add affiliate link"}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              Use the full Awin URL. Public clicks can append affiliate clickref automatically.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Destination">
              <select
                className="input"
                value={form.destination}
                onChange={(e) => updateField("destination", e.target.value)}
              >
                {["Ibiza", "Marbella", "London", "Malta", "Amsterdam"].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </Field>
            <Field label="Category">
              <select
                className="input"
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
              >
                {["hotel", "villa", "transfer", "flight", "experience"].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Partner name">
            <input
              className="input"
              value={form.partner_name}
              onChange={(e) => updateField("partner_name", e.target.value)}
              required
            />
          </Field>

          <Field label="Display title">
            <input
              className="input"
              value={form.display_title}
              onChange={(e) => updateField("display_title", e.target.value)}
              placeholder="Ibiza hotel deals"
              required
            />
          </Field>

          <Field label="Display subtitle">
            <input
              className="input"
              value={form.display_subtitle}
              onChange={(e) => updateField("display_subtitle", e.target.value)}
              placeholder="Hotels near the main clubs"
            />
          </Field>

          <Field label="Awin URL">
            <input
              className="input"
              value={form.awin_url}
              onChange={(e) => updateField("awin_url", e.target.value)}
              placeholder="https://www.awin1.com/..."
              required
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Awin %">
              <input
                type="number"
                step="0.01"
                className="input"
                value={form.commission_pct}
                onChange={(e) => updateField("commission_pct", e.target.value)}
              />
            </Field>
            <Field label="Affiliate %">
              <input
                type="number"
                step="0.01"
                className="input"
                value={form.affiliate_share}
                onChange={(e) => updateField("affiliate_share", e.target.value)}
              />
            </Field>
            <Field label="HLT %">
              <input
                type="number"
                step="0.01"
                className="input"
                value={form.hlt_share}
                onChange={(e) => updateField("hlt_share", e.target.value)}
              />
            </Field>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-300">Calculator sale value</span>
              <input
                type="number"
                className="input w-28"
                value={sampleValue}
                onChange={(e) => setSampleValue(e.target.value)}
              />
            </div>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
              <MiniMetric label="Awin pays" value={money(calculator.commission)} />
              <MiniMetric label="Affiliate gets" value={money(calculator.affiliate)} />
              <MiniMetric label="HLT keeps" value={money(calculator.hlt)} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Sort order">
              <input
                type="number"
                className="input"
                value={form.sort_order}
                onChange={(e) => updateField("sort_order", e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 pt-6 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => updateField("active", e.target.checked)}
              />
              Active
            </label>
          </div>

          <div className="flex gap-2">
            <button className="btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Add link"}
            </button>
            {editingId && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>

        <section className="space-y-4">
          {loading && <div className="card text-slate-300">Loading Awin links...</div>}
          {!loading && links.length === 0 && (
            <div className="card text-slate-300">No Awin links have been added yet.</div>
          )}
          {!loading &&
            links.map((link) => (
              <article key={link.id} className="card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <span className="badge">{link.destination}</span>
                      <span className="badge">{link.category}</span>
                      <span className="badge">{link.partner_name}</span>
                      {!link.active && (
                        <span className="badge bg-red-500/20 text-red-200">Inactive</span>
                      )}
                    </div>
                    <h3 className="mt-2 text-lg font-semibold text-white">{link.display_title}</h3>
                    {link.display_subtitle && (
                      <p className="text-sm text-slate-300">{link.display_subtitle}</p>
                    )}
                    <p className="mt-2 break-all text-xs text-slate-500">{link.awin_url}</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => editLink(link)}>
                      Edit
                    </button>
                    <button className="btn-secondary" onClick={() => removeLink(link.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              </article>
            ))}
        </section>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-medium text-white">{value}</div>
    </div>
  );
}
